/**
 * Outbox change signal — "the device-local capture store was written to".
 *
 * WHY THIS EXISTS (found live 2026-07-27): saving a capture left the Daily Log
 * showing ZERO rows. The record was committed and delivered — the store was
 * correct — but the shell's records state had been read at mount and nothing
 * told it to re-read, so the super saved a capture, opened the log, and saw an
 * empty screen. On the product whose entire promise is "your capture is safe",
 * that reads as data loss.
 *
 * The hook already accepted a `refresh()` that callers were expected to invoke
 * after every mutation, and most call sites did — the capture-commit path
 * simply didn't. That design fails open: every new write path has to remember,
 * and forgetting is invisible until someone looks at the log at the wrong
 * moment. So the signal now comes from the STORE itself (db.ts write helpers),
 * and any reader can subscribe. Missing a refresh now requires forgetting to
 * emit in one shared place rather than in every caller.
 *
 * Deliberately a module-level listener set rather than a DOM CustomEvent: the
 * store is reachable from non-window contexts and unit tests, and this keeps
 * the signal independent of a global object being present.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe to store writes. Returns an unsubscribe function. */
export function onOutboxChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announce that the store changed. Called by the db.ts write helpers after a
 * write settles — never before, so a subscriber that re-reads immediately sees
 * the committed state. Iterates a copy so a listener that unsubscribes during
 * dispatch can't corrupt the iteration, and one throwing listener can't stop
 * the rest from being notified.
 */
export function emitOutboxChanged(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A broken subscriber must not break persistence or the other readers.
    }
  }
}

/** Test-only reset. */
export function __resetOutboxChangeListeners(): void {
  listeners.clear();
}
